import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck, TrendingUp, Scale, HeartHandshake, Sparkles, FileText, Mail, Calendar } from 'lucide-react';
import Reveal from '../components/Reveal';
import PartnersMarquee from '../components/PartnersMarquee';
import { services, approach, articles, images, bookingUrl, regulators } from '../data/mock';

const iconMap = { '01': ShieldCheck, '02': TrendingUp, '03': Scale, '04': HeartHandshake, '05': Sparkles };

const HeroSection = () => (
  <section className="relative min-h-screen flex items-center overflow-hidden">
    <div className="absolute inset-0">
      <img src={images.hero} alt="Paris" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#010c23]/60 via-[#010c23]/50 to-[#010c23]/80" />
    </div>
    <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 lg:px-12 pt-32 pb-20 text-white">
      <Reveal>
        <div className="flex items-center gap-3 mb-8">
          <span className="h-px w-14 bg-[#ff7300]" />
          <span className="text-[11px] tracking-[0.35em] uppercase font-medium text-[#ff7300]">Gestion de patrimoine indépendante</span>
        </div>
      </Reveal>
      <Reveal delay={120}>
        <h1 className="font-serif text-[46px] leading-[1.05] md:text-[72px] md:leading-[1.02] max-w-4xl font-light">
          Votre gestion de patrimoine, <span className="italic text-[#ff7300]">sur mesure</span>
        </h1>
      </Reveal>
      <Reveal delay={240}>
        <p className="mt-8 text-lg md:text-xl text-white/90 max-w-2xl leading-relaxed font-light">
          Chez Ometis Patrimoine, nous vous accompagnons dans l’organisation, la valorisation et la transmission de votre patrimoine, en tenant compte de ses dimensions financières, fiscales et juridiques. À partir d’une analyse globale de votre situation, nous construisons une stratégie sur mesure, adaptée à vos projets de vie et appelée à évoluer avec eux.
        </p>
      </Reveal>
      <Reveal delay={360}>
        <div className="mt-12 flex flex-wrap gap-4">
          <Link to="/contact" className="btn-primary">Nous contacter <Mail size={18} /></Link>
          <a href={bookingUrl} target="_blank" rel="noreferrer" className="btn-outline"><Calendar size={18} /> Prendre rendez-vous en ligne</a>
        </div>
      </Reveal>
    </div>
  </section>
);

const VisionMissionSection = () => (
  <section className="py-28 bg-white">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
      <Reveal>
        <div className="section-label mb-4"><span className="divider-gold"></span>Qui sommes-nous</div>
        <h2 className="font-serif text-4xl md:text-5xl max-w-3xl leading-tight text-[#010c23] font-light">
          Un cabinet indépendant, humain, à votre écoute
        </h2>
      </Reveal>
      <div className="grid md:grid-cols-2 gap-10 mt-16">
        <Reveal>
          <div className="bg-[#fbf9f5] p-10 border-l-2 border-[#ff7300] h-full justify-text">
            <h3 className="font-serif text-2xl mb-4 text-[#010c23]">Notre vision</h3>
            <p className="text-[#010c23]/70 leading-relaxed">
              Nous croyons fermement que chacun mérite une attention particulière et des solutions personnalisées pour répondre à ses besoins spécifiques. Chaque patrimoine raconte une histoire, reflète des choix de vie et répond à des projets différents. Il doit être abordé dans sa globalité, avec écoute, pédagogie et discernement.
            </p>
          </div>
        </Reveal>
        <Reveal delay={120}>
          <div className="bg-[#fbf9f5] p-10 border-l-2 border-[#010c23] h-full justify-text">
            <h3 className="font-serif text-2xl mb-4 text-[#010c23]">Notre mission</h3>
            <p className="text-[#010c23]/70 leading-relaxed">
              Vous accompagner dans l’organisation, le développement et la transmission de votre patrimoine, en coordonnant ses dimensions financières, fiscales et juridiques. Notre rôle est de vous apporter une vision claire, des préconisations adaptées et un suivi régulier, afin de faire évoluer votre stratégie au fil de votre vie.
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const ApproachSection = () => (
  <section className="py-28 bg-[#fbf9f5]">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
      <Reveal>
        <div className="section-label mb-4"><span className="divider-gold"></span>Notre approche</div>
        <h2 className="font-serif text-4xl md:text-5xl text-[#010c23] font-light max-w-2xl leading-tight">
          Une démarche structurée, centrée sur vous
        </h2>
        <p className="mt-6 text-[#010c23]/65 max-w-2xl leading-relaxed">De l’analyse initiale au pilotage dans la durée, chaque étape est structurée, expliquée et partagée avec vous.</p>
      </Reveal>
      <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-5 gap-6 no-justify">
        {approach.map((a, idx) => {
          const Icon = iconMap[a.step];
          return (
            <Reveal key={a.step} delay={idx * 80}>
              <div className="border border-black/10 p-7 h-full hover:border-[#ff7300] transition-colors duration-500 group bg-white">
                <div className="flex items-center justify-between mb-6">
                  <span className="font-serif text-3xl text-[#ff7300]">{a.step}</span>
                  {Icon && <Icon size={22} className="text-[#010c23]/40 group-hover:text-[#ff7300] transition-colors" />}
                </div>
                <h3 className="font-serif text-2xl text-[#010c23] mb-1">{a.title}</h3>
                <p className="text-xs uppercase tracking-widest text-[#010c23]/50 mb-4">{a.subtitle}</p>
                <p className="text-sm text-[#010c23]/70 leading-relaxed">{a.text}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
      <div className="mt-14 text-center">
        <a href={bookingUrl} target="_blank" rel="noreferrer" className="btn-outline-dark"><Calendar size={18} /> Réserver un créneau en ligne avec un conseiller</a>
      </div>
    </div>
  </section>
);

const ServicesPreviewSection = () => (
  <section className="py-28 bg-white">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
      <Reveal>
        <div className="section-label mb-4"><span className="divider-gold"></span>Nos services</div>
        <h2 className="font-serif text-4xl md:text-5xl text-[#010c23] max-w-3xl font-light leading-tight">Des expertises complémentaires, une vision d’ensemble</h2>
      </Reveal>
      <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6 no-justify">
        {services.map((s, idx) => (
          <Reveal key={s.id} delay={idx * 80}>
            <div className="card-service bg-[#fbf9f5] p-8 border border-black/5 h-full flex flex-col">
              <span className="font-serif text-4xl text-[#ff7300]">{s.number}</span>
              <h3 className="font-serif text-2xl text-[#010c23] mt-4">{s.title}</h3>
              <p className="text-xs uppercase tracking-widest text-[#010c23]/50 mt-1">{s.tagline}</p>
              <p className="text-sm text-[#010c23]/70 mt-5 leading-relaxed flex-1">{s.description}</p>
              <Link to={`/services#${s.id}`} className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-[#010c23] hover:text-[#ff7300] transition-colors">Découvrir <ArrowRight size={16} /></Link>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const PartnersSection = () => (
  <section className="py-24 bg-[#fbf9f5]">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 mb-10">
      <div className="section-label mb-4"><span className="divider-gold"></span>Nos partenaires</div>
      <h2 className="font-serif text-4xl md:text-5xl text-[#010c23] font-light max-w-3xl leading-tight">Un écosystème d’acteurs de référence</h2>
    </div>
    <PartnersMarquee />
  </section>
);

const ArticlesSection = () => (
  <section className="py-28 bg-white">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
      <Reveal>
        <div className="flex items-end justify-between flex-wrap gap-6">
          <div>
            <div className="section-label mb-4"><span className="divider-gold"></span>Actualités patrimoniales</div>
            <h2 className="font-serif text-4xl md:text-5xl text-[#010c23] font-light leading-tight">Nos derniers articles</h2>
          </div>
          <Link to="/actualites" className="btn-outline-dark">Tous les articles <ArrowRight size={16} /></Link>
        </div>
      </Reveal>
      <div className="mt-14 grid md:grid-cols-3 gap-6 no-justify">
        {articles.slice(0, 3).map((a, i) => (
          <Reveal key={a.slug} delay={i * 80}>
            <Link to={`/actualites/${a.slug}`} className="group block bg-[#fbf9f5] border border-black/5 hover:border-[#ff7300] transition-colors h-full">
              <div className="aspect-[16/10] overflow-hidden">
                <img src={a.image} alt={a.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="p-7">
                <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-[#010c23]/50">
                  <span className="text-[#ff7300]">{a.category}</span> <span>•</span> <span>{a.readTime}</span>
                </div>
                <h3 className="font-serif text-xl text-[#010c23] mt-3 leading-snug group-hover:text-[#ff7300] transition-colors">{a.title}</h3>
                <p className="text-sm text-[#010c23]/65 mt-3 leading-relaxed line-clamp-3">{a.excerpt}</p>
                <div className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-[#010c23] group-hover:text-[#ff7300] transition-colors">Lire l’article <ArrowRight size={14} /></div>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const RegulatorySection = () => (
  <section className="py-20 bg-[#fbf9f5]">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
      <Reveal>
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="section-label mb-4"><span className="divider-gold"></span>Cadre réglementaire</div>
            <h2 className="font-serif text-4xl md:text-5xl text-[#010c23] font-light leading-tight">Un exercice encadré, en toute transparence</h2>
          </div>
          <div>
            <p className="text-[#010c23]/70 leading-relaxed">Ometis Patrimoine dispose du statut de Conseiller en Investissements Financiers (CIF), Courtier en assurance et Courtier en opérations de banque. Régulé par l’AMF et l’ACPR, inscrit à l’ORIAS (n° 24 004 784), membre de l’ANACOFI.</p>
            <Link to="/reglementaire" className="btn-outline-dark mt-8"><FileText size={16} /> Mentions réglementaires complètes</Link>
          </div>
        </div>
        <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4 no-justify" data-testid="home-regulator-logos">
          {regulators.map((r) => (
            <a key={r.short} href={r.url} target="_blank" rel="noreferrer" className="bg-white border border-black/5 hover:border-[#ff7300] transition-colors p-6 flex flex-col items-center gap-4" data-testid={`home-regulator-${r.short.toLowerCase()}`}>
              <div className="h-14 flex items-center justify-center w-full"><img src={r.logo} alt={r.name} className="max-h-12 max-w-[150px] object-contain" /></div>
              <p className="text-[10px] tracking-wider uppercase text-[#010c23]/55 text-center leading-snug">{r.role}</p>
            </a>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);

const FinalCtaSection = () => (
  <section className="relative py-32 overflow-hidden">
    <div className="absolute inset-0">
      <img src={images.boardroom} alt="" loading="lazy" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-[#010c23]/80" />
    </div>
    <div className="relative z-10 max-w-[1000px] mx-auto px-6 text-center text-white">
      <Reveal>
        <h2 className="font-serif text-4xl md:text-5xl font-light leading-tight">Agissez dès maintenant pour <span className="italic text-[#ff7300]">sécuriser</span> votre avenir</h2>
        <p className="mt-8 text-white/85 max-w-2xl mx-auto leading-relaxed">Dans un environnement en constante évolution, ajuster ses investissements, optimiser sa fiscalité et protéger sa famille exigent d’anticiper. Ometis Patrimoine vous accompagne à chaque étape, avec expertise et proximité, pour transformer vos objectifs en résultats concrets.</p>
        <div className="mt-10 flex flex-wrap justify-center gap-4">
          <Link to="/contact" className="btn-primary">Nous contacter <Mail size={18} /></Link>
          <a href={bookingUrl} target="_blank" rel="noreferrer" className="btn-outline"><Calendar size={18} /> Prendre rendez-vous en ligne</a>
        </div>
      </Reveal>
    </div>
  </section>
);

const Home = () => (
  <div>
    <HeroSection />
    <VisionMissionSection />
    <ApproachSection />
    <ServicesPreviewSection />
    <PartnersSection />
    <ArticlesSection />
    <RegulatorySection />
    <FinalCtaSection />
  </div>
);

export default Home;
